import React, { useEffect, useRef } from 'react';

import leftIcon from '../assets/mobile-left.png';
import rightIcon from '../assets/mobile-right.png';
import gasIcon from '../assets/mobile-gas.png';

/**
 * Touch controls for phones/tablets. Renders left/right steering arrows on the
 * bottom-left and an accelerator on the bottom-right. Under the hood each
 * button dispatches synthetic ArrowLeft / ArrowRight / ArrowUp keyboard events
 * so the existing GameScene input handler picks them up with zero changes.
 */
const KEY_META = {
  ArrowLeft: { code: 'ArrowLeft', keyCode: 37 },
  ArrowRight: { code: 'ArrowRight', keyCode: 39 },
  ArrowUp: { code: 'ArrowUp', keyCode: 38 },
  ArrowDown: { code: 'ArrowDown', keyCode: 40 }
};

const dispatchKey = (type, key) => {
  const meta = KEY_META[key] || { code: key, keyCode: 0 };
  // Older Safari + WebKit ignore KeyboardEvents whose keyCode is 0; setting
  // both `code` and the legacy `keyCode`/`which` fields makes the synthetic
  // event indistinguishable from a real key press across browsers.
  const evt = new KeyboardEvent(type, {
    key,
    code: meta.code,
    keyCode: meta.keyCode,
    which: meta.keyCode,
    bubbles: true,
    cancelable: true
  });
  try {
    Object.defineProperty(evt, 'keyCode', { value: meta.keyCode });
    Object.defineProperty(evt, 'which', { value: meta.keyCode });
  } catch (_) { /* read-only in some engines — safe to ignore */ }
  window.dispatchEvent(evt);
};

const useHoldKey = (key) => {
  const activeRef = useRef(false);

  const start = (e) => {
    if (e && e.cancelable) e.preventDefault();
    if (activeRef.current) return;
    activeRef.current = true;
    dispatchKey('keydown', key);
  };

  const end = (e) => {
    if (e && e.cancelable) e.preventDefault();
    if (!activeRef.current) return;
    activeRef.current = false;
    dispatchKey('keyup', key);
  };

  useEffect(() => {
    return () => {
      if (activeRef.current) dispatchKey('keyup', key);
    };
  }, [key]);

  return {
    onPointerDown: start,
    onPointerUp: end,
    onPointerCancel: end,
    onPointerLeave: end,
    onTouchStart: start,
    onTouchEnd: end,
    onTouchCancel: end,
    onMouseDown: start,
    onMouseUp: end,
    onMouseLeave: end,
    onContextMenu: (e) => e.preventDefault()
  };
};

const iconButtonStyle = {
  position: 'absolute',
  padding: 0,
  border: 'none',
  background: 'transparent',
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  userSelect: 'none',
  touchAction: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 60
};

const imgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  pointerEvents: 'none',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
  filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.65))'
};

const MobileControls = () => {
  const leftHandlers = useHoldKey('ArrowLeft');
  const rightHandlers = useHoldKey('ArrowRight');
  const upHandlers = useHoldKey('ArrowUp');

  return (
    <div
      className="rc-mobile-only"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 60
      }}
    >
      {/* Left arrow — bottom-left floating above Card Dashboard */}
      <button
        {...leftHandlers}
        data-testid="mobile-steer-left"
        aria-label="Steer left"
        style={{
          ...iconButtonStyle,
          left: 'clamp(12px, 2.5vw, 24px)',
          bottom: 'clamp(72px, 18vh, 98px)',
          width: 'clamp(68px, 12.5vw, 92px)',
          height: 'clamp(68px, 12.5vw, 92px)',
          pointerEvents: 'auto'
        }}
      >
        <img src={leftIcon} alt="" draggable={false} style={imgStyle} />
      </button>

      {/* Right arrow — next to left arrow floating above Card Dashboard */}
      <button
        {...rightHandlers}
        data-testid="mobile-steer-right"
        aria-label="Steer right"
        style={{
          ...iconButtonStyle,
          left: 'clamp(86px, 15vw, 124px)',
          bottom: 'clamp(72px, 18vh, 98px)',
          width: 'clamp(68px, 12.5vw, 92px)',
          height: 'clamp(68px, 12.5vw, 92px)',
          pointerEvents: 'auto'
        }}
      >
        <img src={rightIcon} alt="" draggable={false} style={imgStyle} />
      </button>

      {/* Accelerator — bottom-right floating above Card Dashboard */}
      <button
        {...upHandlers}
        data-testid="mobile-accelerate"
        aria-label="Accelerate"
        style={{
          ...iconButtonStyle,
          right: 'clamp(12px, 2.5vw, 24px)',
          bottom: 'clamp(72px, 18vh, 98px)',
          width: 'clamp(76px, 14.5vw, 102px)',
          height: 'clamp(76px, 14.5vw, 102px)',
          pointerEvents: 'auto'
        }}
      >
        <img src={gasIcon} alt="" draggable={false} style={imgStyle} />
      </button>
    </div>
  );
};

export default MobileControls;
